/**
 * Reporting Fallback Component
 * 
 * Provides alternative functionality when the reporting microservice is unavailable
 * Redirects to legacy reporting system or shows offline capabilities
 */

import React from 'react';
import { Card, Button, Row, Col, Alert } from 'react-bootstrap';
import { 
    BsFileText, 
    BsArrowRight, 
    BsDownload, 
    BsTable,
    BsClockHistory,
    BsShieldCheck
} from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';

const ReportingFallback = ({ 
    showLegacyOptions = true,
    showOfflineMessage = true 
}) => {
    const navigate = useNavigate();

    const legacyReportOptions = [
        {
            title: 'A1 Patient Reports',
            description: 'Generate A1 patient note reports',
            path: '/a1report',
            icon: BsFileText,
            variant: 'primary'
        },
        {
            title: 'A46 Reports',
            description: 'Generate A46 administrative reports',
            path: '/A46report',
            icon: BsTable,
            variant: 'success'
        },
        {
            title: 'Report Dashboard',
            description: 'Access hemodialysis report dashboard',
            path: '/reportDashboard',
            icon: BsClockHistory,
            variant: 'info'
        },
        {
            title: 'View Reports List',
            description: 'Browse existing report records',
            path: '/reportslist',
            icon: BsDownload,
            variant: 'warning'
        }
    ];

    return (
        <div className="reporting-fallback">
            {showOfflineMessage && (
                <Alert variant="info" className="mb-4">
                    <div className="d-flex align-items-center">
                        <BsShieldCheck className="text-info me-3 h4 mb-0" />
                        <div>
                            <Alert.Heading className="h6 mb-1">Alternative Reporting Available</Alert.Heading>
                            <p className="mb-0">
                                While the advanced reporting service is unavailable, you can still access 
                                our legacy reporting system for essential reporting functions.
                            </p>
                        </div>
                    </div>
                </Alert>
            )}

            {showLegacyOptions && (
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-transparent border-0 pb-2">
                        <h5 className="mb-0 fw-semibold">Available Reporting Options</h5>
                        <small className="text-muted">
                            Access these reporting features while the advanced service is being restored
                        </small>
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-3">
                            {legacyReportOptions.map((option, index) => {
                                const Icon = option.icon;
                                
                                return (
                                    <Col key={index} md={6} lg={3}>
                                        <Card className="h-100 border-0 shadow-sm option-card">
                                            <Card.Body className="text-center">
                                                <div className={`option-icon bg-${option.variant} bg-opacity-10 rounded-circle p-3 mx-auto mb-3`} style={{ width: '60px', height: '60px' }}>
                                                    <Icon className={`text-${option.variant} h4 mb-0`} />
                                                </div>
                                                <h6 className="fw-semibold mb-2">{option.title}</h6>
                                                <p className="text-muted small mb-3">
                                                    {option.description}
                                                </p>
                                                <Button 
                                                    variant={option.variant}
                                                    size="sm"
                                                    className="w-100"
                                                    onClick={() => navigate(option.path)}
                                                >
                                                    Access <BsArrowRight className="ms-1" />
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>

                        <hr className="my-4" />

                        <Row>
                            <Col>
                                <Alert variant="light" className="mb-0">
                                    <h6 className="fw-semibold mb-2">What's Available in Legacy Mode:</h6>
                                    <Row className="g-2">
                                        <Col md={6}>
                                            <ul className="list-unstyled mb-0 small">
                                                <li>✓ Form-based patient reports</li>
                                                <li>✓ Hemodialysis tracking reports</li>
                                                <li>✓ Administrative reports (A1, A46)</li>
                                            </ul>
                                        </Col>
                                        <Col md={6}>
                                            <ul className="list-unstyled mb-0 small">
                                                <li>✓ Date range filtering</li>
                                                <li>✓ PDF export capabilities</li>
                                                <li>✓ Print-friendly formats</li>
                                            </ul>
                                        </Col>
                                    </Row>
                                </Alert>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            <style jsx>{`
                .option-card {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .option-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
                }
            `}</style>
        </div>
    );
};

export default ReportingFallback;